<!-- SoundsVideo - Professional Video & Sound Preview Tool -->
<div class="soundsvideo-app" [class.fullscreen]="isFullscreen" [class.sidebar-collapsed]="!showSidebar" [class.is-mobile]="isMobile">

  <!-- Top Toolbar - Desktop App Style -->
  <header class="app-toolbar">
    <div class="toolbar-left">
      <button class="toolbar-btn toggle-sidebar-btn" (click)="toggleSidebar()" [attr.title]="'SOUNDSVIDEO.TOOLBAR.TOGGLE_SIDEBAR' | translate">
        <i class="fas" [ngClass]="showSidebar ? 'fa-chevron-down' : 'fa-music'"></i>
      </button>
      <div class="app-branding">
        <span class="app-logo">
          <i class="fas fa-film"></i>
        </span>
        <h1>{{ 'SOUNDSVIDEO.TITLE' | translate }}</h1>
      </div>
    </div>

    <div class="toolbar-center">
      <!-- Logo central - Retour à home -->
      <a routerLink="/" class="center-logo-link" title="Home">
        <img src="assets/images/png_applisound/applisound-blanc.png" alt="Applisound" class="center-logo" draggable="false">
      </a>
      @if (canPlayBoth) {
        <div class="sync-status-badge" [ngClass]="getSyncStatusClass()">
          <i class="fas fa-link"></i>
          <span>{{ getSyncStatusText() }}</span>
        </div>
      }
    </div>

    <div class="toolbar-right">
      <button class="toolbar-btn" (click)="openTipsModal()" [attr.title]="'SOUNDSVIDEO.TIPS.TITLE' | translate">
        <i class="fas fa-keyboard"></i>
      </button>
      <button class="toolbar-btn" (click)="openInfoModal()" [attr.title]="'SOUNDSVIDEO.INFO.TITLE' | translate">
        <i class="fas fa-info-circle"></i>
      </button>
      <button class="toolbar-btn" (click)="toggleFullscreen()" [attr.title]="'SOUNDSVIDEO.TOOLBAR.FULLSCREEN' | translate">
        <i class="fas" [ngClass]="isFullscreen ? 'fa-compress' : 'fa-expand'"></i>
      </button>
    </div>
  </header>

  <!-- Main Content Area -->
  <div class="app-content">

    <!-- Sidebar - Music Library & Upload -->
    <aside class="sidebar" [class.collapsed]="!showSidebar">
      <div class="sidebar-tabs">
        <button class="tab-btn" [class.active]="activeTab === 'library'" (click)="setActiveTab('library')">
          <i class="fas fa-music"></i>
          {{ 'SOUNDSVIDEO.SIDEBAR.LIBRARY' | translate }}
        </button>
        <button class="tab-btn" [class.active]="activeTab === 'upload'" (click)="setActiveTab('upload')">
          <i class="fas fa-upload"></i>
          {{ 'SOUNDSVIDEO.SIDEBAR.UPLOAD' | translate }}
        </button>
      </div>

      <!-- Music Library Tab -->
      @if (activeTab === 'library') {
        <div class="sidebar-content">
          <div class="search-filters">
            <div class="search-box">
              <i class="fas fa-search"></i>
              <input
                type="text"
                [(ngModel)]="searchQuery"
                [placeholder]="'SOUNDSVIDEO.SIDEBAR.SEARCH_PLACEHOLDER' | translate"
                />
              </div>
              <div class="mood-filter">
                <select [(ngModel)]="selectedMood">
                  @for (mood of moods; track mood) {
                    <option [value]="mood">
                      {{ mood === 'all' ? ('SOUNDSVIDEO.SIDEBAR.ALL_MOODS' | translate) : mood }}
                    </option>
                  }
                </select>
              </div>
            </div>
            <div class="track-count">
              {{ filteredTracks.length }} {{ 'SOUNDSVIDEO.SIDEBAR.TRACKS' | translate }}
            </div>
            <div class="music-library">
              @for (track of filteredTracks; track track) {
                <div class="track-item"
                  [class.selected]="selectedTrack?.id === track.id"
                  [class.playing]="selectedTrack?.id === track.id && isMusicPlaying"
                  (click)="selectTrack(track)">
                  <div class="track-artwork" [class.has-cover]="!!track.imageUrl">
                    @if (track.imageUrl) {
                      <img class="cover-image" [src]="track.imageUrl" [alt]="track.title" loading="lazy" />
                    }
                    <div class="track-gradient" [style.background]="getTrackGradient(track)"></div>
                    <div class="play-overlay">
                      <i class="fas" [ngClass]="selectedTrack?.id === track.id && isMusicPlaying ? 'fa-pause' : 'fa-play'"></i>
                    </div>
                    @if (selectedTrack?.id === track.id && isMusicPlaying) {
                      <div class="wave-indicator">
                        <span></span><span></span><span></span><span></span>
                      </div>
                    }
                  </div>
                  <div class="track-info">
                    <h4>{{ track.title }}</h4>
                    <p>{{ track.artist }}</p>
                    <div class="track-meta">
                      <span class="duration"><i class="fas fa-clock"></i> {{ track.duration }}</span>
                      @if (track.mood && track.mood[0]) {
                        <span class="mood-tag">{{ track.mood[0] }}</span>
                      }
                    </div>
                  </div>
                </div>
              }
              @if (filteredTracks.length === 0) {
                <div class="empty-state">
                  <i class="fas fa-search"></i>
                  <p>{{ 'SOUNDSVIDEO.SIDEBAR.NO_TRACKS' | translate }}</p>
                </div>
              }
            </div>
          </div>
        }

        <!-- Upload Tab -->
        @if (activeTab === 'upload') {
          <div class="sidebar-content">
            <div class="upload-zone"
              (dragover)="onDragOver($event)"
              (dragleave)="onDragLeave($event)"
              (drop)="onDrop($event)"
              [class.drag-over]="isDragOver"
              (click)="triggerFileInput()">
              <input #fileInput type="file" accept="video/*" (change)="onFileSelected($event)" style="display: none;">
              <div class="upload-icon">
                <i class="fas fa-cloud-upload-alt"></i>
              </div>
              <h3>{{ 'SOUNDSVIDEO.UPLOAD.HEADER' | translate }}</h3>
              <p>{{ 'SOUNDSVIDEO.UPLOAD.SUBTEXT' | translate }}</p>
              <span class="formats">{{ 'SOUNDSVIDEO.UPLOAD.FORMATS' | translate }}</span>
            </div>
            @if (currentVideo) {
              <div class="current-video-info">
                <div class="video-file-card">
                  <div class="file-icon">
                    <i class="fas fa-file-video"></i>
                  </div>
                  <div class="file-details">
                    <h4>{{ currentVideo.name }}</h4>
                    <div class="file-meta">
                      <span><i class="fas fa-clock"></i> {{ formatTime(currentVideo.duration) }}</span>
                      <span><i class="fas fa-hdd"></i> {{ formatFileSize(currentVideo.size) }}</span>
                    </div>
                  </div>
                  <button class="remove-btn" (click)="removeVideo(); $event.stopPropagation()">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </div>
            }
          </div>
        }
      </aside>

      <!-- Main Stage - Video Player -->
      <main class="video-stage" #videoSection>
        <!-- Empty State -->
        @if (!currentVideo) {
          <div class="empty-stage"
            (dragover)="onDragOver($event)"
            (dragleave)="onDragLeave($event)"
            (drop)="onDrop($event)"
            [class.drag-over]="isDragOver">
            <div class="empty-content">
              <div class="empty-icon">
                <i class="fas fa-video"></i>
              </div>
              <h2>{{ 'SOUNDSVIDEO.STAGE.EMPTY_TITLE' | translate }}</h2>
              <p>{{ 'SOUNDSVIDEO.STAGE.EMPTY_SUBTITLE' | translate }}</p>
              <button class="upload-btn-primary" (click)="triggerFileInput()">
                <i class="fas fa-upload"></i>
                {{ 'SOUNDSVIDEO.STAGE.UPLOAD_BTN' | translate }}
              </button>
              <input #fileInput type="file" accept="video/*" (change)="onFileSelected($event)" style="display: none;">
            </div>
          </div>
        }

        <!-- Video Player -->
        @if (currentVideo) {
          <div class="video-player-wrapper" (mouseenter)="onVideoMouseEnter()" (mouseleave)="onVideoMouseLeave()">
            <video #videoPlayer
              [src]="currentVideo.url"
              (loadedmetadata)="onVideoLoaded()"
              (timeupdate)="onVideoTimeUpdate()"
              (ended)="onVideoEnded()"
              (click)="toggleVideoPlay()"
              [loop]="isVideoLoop"
              preload="metadata">
            </video>
            <!-- Video Overlay Controls -->
            <div class="video-overlay" [class.show-controls]="showVideoControls">
              <div class="top-bar">
                <span class="video-title">{{ currentVideo.name }}</span>
                <button class="close-video-btn" (click)="removeVideo()">
                  <i class="fas fa-times"></i>
                </button>
              </div>
              @if (!isVideoPlaying) {
                <div class="center-play" (click)="toggleVideoPlay()">
                  <i class="fas fa-play"></i>
                </div>
              }
              <div class="bottom-controls">
                <!-- Video Timeline -->
                <div class="timeline-container">
                  <div class="timeline-bar video-bar"
                    (click)="seekVideo($event)"
                    (mousedown)="startVideoDrag($event)"
                    (touchstart)="startVideoDrag($event)">
                    <div class="timeline-progress" [style.width]="videoProgress + '%'"></div>
                    <div class="timeline-handle" [style.left]="videoProgress + '%'"></div>
                  </div>
                </div>
                <div class="controls-row">
                  <div class="controls-left">
                    <button class="control-btn" (click)="toggleVideoPlay(); $event.stopPropagation()">
                      <i class="fas" [ngClass]="isVideoPlaying ? 'fa-pause' : 'fa-play'"></i>
                    </button>
                    <span class="time-display">
                      {{ formatTime(currentVideoTime) }} / {{ formatTime(videoDuration) }}
                    </span>
                  </div>
                  <div class="controls-right">
                    <!-- Loop/Repeat button -->
                    <button class="control-btn" [class.active]="isVideoLoop" (click)="toggleVideoLoop(); $event.stopPropagation()" title="Repeat">
                      <i class="fas fa-redo"></i>
                    </button>
                    <!-- Fullscreen button -->
                    <button class="control-btn" (click)="enterVideoFullscreen(); $event.stopPropagation()" title="Fullscreen">
                      <i class="fas fa-expand"></i>
                    </button>
                    <div class="volume-control">
                      <i class="fas" [ngClass]="videoVolume === 0 ? 'fa-volume-mute' : 'fa-volume-up'"></i>
                      <input type="range" min="0" max="100" [(ngModel)]="videoVolume" (input)="updateVideoVolume()">
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
      </main>
    </div>

    <!-- Bottom Control Bar - Music & Sync -->
    @if (selectedTrack || currentVideo) {
      <footer class="control-bar">
        <!-- Selected Track Info -->
        @if (selectedTrack) {
          <div class="now-playing">
            <div class="track-mini-artwork" [class.has-cover]="!!selectedTrack.imageUrl">
              @if (selectedTrack.imageUrl) {
                <img [src]="selectedTrack.imageUrl" [alt]="selectedTrack.title" />
              }
              <div class="mini-gradient" [style.background]="getTrackGradient(selectedTrack)"></div>
            </div>
            <div class="track-mini-info">
              <h4>{{ selectedTrack.title }}</h4>
              <p>{{ selectedTrack.artist }}</p>
            </div>
          </div>
        }
        <!-- Main Playback Controls -->
        <div class="playback-controls">
          <button class="playback-btn" (click)="resetBoth()" [disabled]="!canPlayBoth">
            <i class="fas fa-undo"></i>
          </button>
          <button class="playback-btn main-play" (click)="togglePlayBoth()" [disabled]="!canPlayBoth">
            <i class="fas" [ngClass]="(isVideoPlaying && isMusicPlaying) ? 'fa-pause' : 'fa-play'"></i>
          </button>
        </div>
        <!-- Music Timeline & Volume -->
        @if (selectedTrack) {
          <div class="music-controls">
            <div class="music-timeline">
              <span class="time-label">{{ formatTime(currentMusicTime) }}</span>
              <div class="timeline-bar music-bar"
                (click)="seekMusic($event)"
                (mousedown)="startMusicDrag($event)"
                (touchstart)="startMusicDrag($event)">
                <div class="timeline-progress" [style.width]="musicProgress + '%'"></div>
                <div class="timeline-handle" [style.left]="musicProgress + '%'"></div>
              </div>
              <span class="time-label">{{ selectedTrack.duration }}</span>
            </div>
            <!-- Music Loop Button -->
            <button class="music-loop-btn" [class.active]="isMusicLoop" (click)="toggleMusicLoop()" title="Repeat Music">
              <i class="fas fa-redo"></i>
            </button>
            <div class="music-volume">
              <i class="fas fa-music"></i>
              <input type="range" min="0" max="100" [(ngModel)]="musicVolume" (input)="updateMusicVolume()">
            </div>
          </div>
        }
        <!-- CTA Button -->
        @if (canPlayBoth) {
          <div class="cta-section">
            <button class="cta-btn" (click)="orderCustomMusic()">
              <i class="fas fa-shopping-cart"></i>
              {{ 'SOUNDSVIDEO.ACTIONS.ORDER_CUSTOM' | translate }}
            </button>
          </div>
        }
      </footer>
    }

    <!-- Tips Modal -->
    @if (showTipsModal) {
      <div class="modal-overlay" [class.closing]="isClosing" (click)="closeTipsModal()">
        <div class="modal-content tips-modal" (click)="$event.stopPropagation()">
          <div class="modal-header">
            <h3><i class="fas fa-keyboard"></i> {{ 'SOUNDSVIDEO.TIPS.TITLE' | translate }}</h3>
            <button class="close-modal-btn" (click)="closeTipsModal()">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="modal-body">
            <div class="shortcuts-grid">
              <div class="shortcut-item">
                <kbd>Space</kbd>
                <span>{{ 'SOUNDSVIDEO.TIPS.PLAY_PAUSE' | translate }}</span>
              </div>
              <div class="shortcut-item">
                <kbd>F</kbd>
                <span>{{ 'SOUNDSVIDEO.TIPS.FULLSCREEN' | translate }}</span>
              </div>
              <div class="shortcut-item">
                <kbd>S</kbd>
                <span>{{ 'SOUNDSVIDEO.TIPS.TOGGLE_SIDEBAR' | translate }}</span>
              </div>
              <div class="shortcut-item">
                <kbd>R</kbd>
                <span>{{ 'SOUNDSVIDEO.TIPS.RESET' | translate }}</span>
              </div>
              <div class="shortcut-item">
                <kbd>Esc</kbd>
                <span>{{ 'SOUNDSVIDEO.TIPS.EXIT_FULLSCREEN' | translate }}</span>
              </div>
            </div>
            <div class="tips-list">
              <h4>{{ 'SOUNDSVIDEO.TIPS.PRO_TIPS' | translate }}</h4>
              <ul>
                <li>{{ 'SOUNDSVIDEO.TIPS.L1' | translate }}</li>
                <li>{{ 'SOUNDSVIDEO.TIPS.L2' | translate }}</li>
                <li>{{ 'SOUNDSVIDEO.TIPS.L3' | translate }}</li>
                <li>{{ 'SOUNDSVIDEO.TIPS.L4' | translate }}</li>
              </ul>
            </div>
          </div>
          <div class="modal-footer">
            <button class="modal-btn primary" (click)="closeTipsModal()">{{ 'SOUNDSVIDEO.TIPS.OK' | translate }}</button>
          </div>
        </div>
      </div>
    }

    <!-- Info Modal -->
    @if (showInfoModal) {
      <div class="modal-overlay" [class.closing]="isClosing" (click)="closeInfoModal()">
        <div class="modal-content info-modal" (click)="$event.stopPropagation()">
          <div class="modal-header">
            <h3><i class="fas fa-info-circle"></i> {{ 'SOUNDSVIDEO.INFO.TITLE' | translate }}</h3>
            <button class="close-modal-btn" (click)="closeInfoModal()">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="modal-body">
            <p class="info-description">{{ 'SOUNDSVIDEO.INFO.DESCRIPTION' | translate }}</p>
            <div class="features-grid">
              <div class="feature-item">
                <i class="fas fa-shield-alt"></i>
                <h4>{{ 'SOUNDSVIDEO.INFO.PRIVATE_TITLE' | translate }}</h4>
                <p>{{ 'SOUNDSVIDEO.INFO.PRIVATE_DESC' | translate }}</p>
              </div>
              <div class="feature-item">
                <i class="fas fa-sync-alt"></i>
                <h4>{{ 'SOUNDSVIDEO.INFO.SYNC_TITLE' | translate }}</h4>
                <p>{{ 'SOUNDSVIDEO.INFO.SYNC_DESC' | translate }}</p>
              </div>
              <div class="feature-item">
                <i class="fas fa-music"></i>
                <h4>{{ 'SOUNDSVIDEO.INFO.LIBRARY_TITLE' | translate }}</h4>
                <p>{{ 'SOUNDSVIDEO.INFO.LIBRARY_DESC' | translate }}</p>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="modal-btn primary" (click)="closeInfoModal()">{{ 'SOUNDSVIDEO.INFO.OK' | translate }}</button>
          </div>
        </div>
      </div>
    }
  </div>
